import { useMutation, useQueryClient } from '@tanstack/react-query';
import { i18n } from '@/shared/i18n';
import { mediaHttp } from '@/lib/api/http-client';
import { uploadToS3 } from '@/shared/api/upload-to-s3';
import {
  CREATIVE_FIELD_PREFIX,
  creativeFieldKey,
  isCreativeField,
} from '../model/platform-structure';
import type {
  DeliveryJob,
  JobPlan,
  JobPlatformGroup,
  JobPublishReport,
  JobSourceKind,
} from '../model/job.types';
import { uploadJobAssets } from './job-assets';
import { deliveryJobQueryOptions } from './job-queries';
import { toDeliveryJob, toJobPlan, toPublishReport } from './job-mappers';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import { parseResponse } from '@/lib/api/parse-response';
import {
  jobPlanSchema,
  jobPublishReportSchema,
  jobSchema,
  preflightSchema,
  presignedAssetsSchema,
  type ConfirmedAsset,
} from './schemas';

export interface CreateJobInput {
  title: string;
  /**
   * Every platform the job may cover. The sheet decides which it actually does,
   * and the upload drops the ones it finds no rows for — so binding a single
   * platform here silently discards the rest of a multi-platform plan.
   */
  platforms: string[];
  file: File;
  /** The creatives the plan names. Empty for an archive, which carries its own. */
  creatives: File[];
  /** Fraction of a zip sent to storage, from 0 to 1. A sheet reports nothing. */
  onUploadProgress?: (fraction: number) => void;
}

export interface CreatedJob {
  job: DeliveryJob;
  /**
   * One outcome per creative sent, settling after the job is already open.
   *
   * A promise rather than the outcomes themselves because the bytes are still going up when
   * this mutation resolves. Held until the last creative landed, the dialog sat on "Parsing"
   * for the length of the upload — up to a gigabyte of video, with no progress and no cancel,
   * describing a call that had finished minutes earlier - and a PUT that stalled rather than
   * failing never released it at all, leaving a permanent dialog over a job that did exist
   * and a second one on every re-submit.
   *
   * Never rejects, so a caller may attach to it without a catch.
   */
  creatives: Promise<ConfirmedAsset[]>;
  /**
   * For a zip only: the job once the worker has finished reading it, with `sourceError`
   * set if it did not become a plan. Still `isReadingSource` if it outlasted the wait.
   * Never rejects.
   */
  ingested?: Promise<DeliveryJob>;
}

/** Long enough for a hundred megabytes on a single worker behind other uploads. */
const INGEST_WAIT_MS = 15 * 60 * 1000;
const INGEST_POLL_MS = 3000;

export interface EditNodeFieldInput {
  nodeId: string;
  key: string;
  value: string | null;
}

export interface BindJobAccountInput {
  platform: string;
  connectorId: string;
  accountName: string;
}

export interface BindJobPageInput {
  platform: string;
  pageId: string;
  pageName: string;
}

export interface BindJobAdvertiserInput {
  platform: string;
  /** Null goes back to the account's own advertiser. */
  advertiserId: string | null;
}

/**
 * Declared up front, before the server has seen the bytes, so it is read off the name.
 * The upload corrects it either way: only the parse knows what was really in there.
 */
function sourceFileKind(fileName: string): JobSourceKind {
  const name = fileName.toLowerCase();
  if (name.endsWith('.zip')) return 'zip';
  return name.endsWith('.csv') ? 'csv' : 'xlsx';
}

/**
 * Creating a Job is two calls: the record, then the file.
 *
 * Split because the upload needs somewhere to write, and because a re-upload of a
 * corrected spreadsheet is then the same request as the first one. A file that fails
 * to parse leaves the Job behind on purpose — the reviewer can drop a fixed sheet on
 * it rather than starting over.
 *
 * Settles on the sheet alone. The creatives keep uploading behind the job the caller has
 * already been sent to; see {@link CreatedJob.creatives}.
 */
export function useCreateJob(projectId: string) {
  const queryClient = useQueryClient();

  /**
   * A zip goes straight to storage and is read on a worker: unpacked inside the request, a
   * hundred megabytes of creatives outlived the load balancer and came back as a 502.
   */
  async function sendArchive(
    jobId: string,
    file: File,
    onUploadProgress?: (fraction: number) => void,
  ): Promise<void> {
    const target = parseResponse(
      presignedAssetsSchema.element,
      await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/source/upload-url`,
        body: { fileName: file.name },
      }),
    );
    await uploadToS3(target.uploadUrl, target.uploadFields, file, undefined, (loaded, total) =>
      onUploadProgress?.(loaded / total),
    );
    await mediaHttp.post({
      security: [{ scheme: 'bearer', type: 'http' }],
      url: `/api/v1/delivery-jobs/${jobId}/source/actions/ingest`,
      body: { assetId: target.assetId },
    });
  }

  async function waitForIngest(created: DeliveryJob): Promise<DeliveryJob> {
    const deadline = Date.now() + INGEST_WAIT_MS;
    let job: DeliveryJob = { ...created, isReadingSource: true };
    while (job.isReadingSource && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, INGEST_POLL_MS));
      const previous = job;
      job = await queryClient
        .fetchQuery({ ...deliveryJobQueryOptions(created.id, projectId), staleTime: 0 })
        .catch(() => previous);
    }

    void queryClient.invalidateQueries({
      queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobPlan(created.id),
    });
    void queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.allJobs() });
    return job;
  }

  return useMutation({
    mutationFn: async (input: CreateJobInput) => {
      const created = await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/delivery-jobs',
        body: {
          projectId,
          title: input.title,
          sourceFileName: input.file.name,
          sourceFileKind: sourceFileKind(input.file.name),
          platforms: input.platforms.map((platform) => ({ platform })),
        },
      });

      const job = toDeliveryJob(parseResponse(jobSchema, created));

      // Started before the sheet goes up, so thirty megabytes of images do not wait on a
      // parse they take no part in, and deliberately not awaited: the plan is the job, and
      // the reviewer belongs on it as soon as it parses rather than when the last creative
      // lands. A creative that fails must not fail the creation either — it is an attachment
      // the reviewer can retry a row at a time. `uploadJobAssets` reports one outcome per
      // file and never rejects, so nothing here has to catch for it.
      const creatives = uploadJobAssets(job.id, input.creatives).then((outcomes) => {
        // The job page fetched its plan while these were still uploading, so without this
        // the rows it drew keep showing no creative until something else refetches.
        if (outcomes.some((outcome) => outcome.status === 'stored')) {
          void queryClient.invalidateQueries({
            queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobPlan(job.id),
          });
          void queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.allJobs() });
        }
        return outcomes;
      });

      if (sourceFileKind(input.file.name) === 'zip') {
        await sendArchive(job.id, input.file, input.onUploadProgress);
        return { job, creatives, ingested: waitForIngest(job) };
      }

      const form = new FormData();
      form.append('file', input.file);
      await mediaHttp.post({
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${job.id}/source`,
        body: form,
      });

      return { job, creatives };
    },
    // Settled, not success: creation is two calls and the record exists before the
    // upload runs, so a rejected sheet still leaves a job the reviewer is meant to
    // find and drop a corrected file onto. On success only, that job stays invisible
    // until a hard reload.
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobs(projectId) }),
  });
}

/**
 * Writes the returned plan into the cache rather than invalidating. Re-fetching
 * after every committed field would collapse the tree's expansion state under the
 * reviewer, which is unusable on a plan this size.
 */
function useWritePlan(jobId: string) {
  const queryClient = useQueryClient();
  return (plan: JobPlan) =>
    queryClient.setQueryData(MEDIA_CAMPAIGN_QUERY_KEYS.jobPlan(jobId), plan);
}

/**
 * Applies one field edit, to the ad or to the creative it references.
 *
 * The table shows a creative's values on its ad's row under a prefix, so the prefix
 * is what decides which object the PATCH addresses.
 */
export function useEditNodeField(jobId: string) {
  const queryClient = useQueryClient();
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async (input: EditNodeFieldInput) => {
      const plan = queryClient.getQueryData<JobPlan>(MEDIA_CAMPAIGN_QUERY_KEYS.jobPlan(jobId));
      const onCreative = isCreativeField(input.key);
      const nodeId = onCreative ? creativeIdOf(plan, input.nodeId) : input.nodeId;
      const key = onCreative ? creativeFieldKey(input.key) : input.key;

      if (!nodeId) {
        throw new Error(`No creative attached to this ${CREATIVE_FIELD_PREFIX.slice(0, -1)} row`);
      }

      // Every level requires a name, so clearing one is a rejection either way. Caught
      // here it is a sentence in the reviewer's own terms; sent, it came back as a 400
      // whose useful half never reached the screen.
      if (key === 'name' && (input.value ?? '').trim() === '') {
        throw new Error(i18n.t('projects:mediaCampaign.nameCannotBeEmpty'));
      }

      const body =
        key === 'name' ? { name: input.value ?? '' } : { fields: { [key]: input.value } };

      await mediaHttp.patch({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/nodes/${nodeId}`,
        body,
      });

      return fetchPlan(jobId);
    },
    onSuccess: writePlan,
  });
}

export interface PreflightResult {
  errors: number;
  warnings: number;
  unboundPlatforms: string[];
  /**
   * Bound accounts a plan cannot go out through, which no rule can see.
   *
   * `reason` and `detail` are both load-bearing and were both dropped here once. An
   * account with no advertiser is unfinished, not broken, and telling someone to
   * reconnect it sends them to redo the one step that would not fix it. `detail` is the
   * platform's own words about a failed sign-in, which beat anything we would paraphrase.
   */
  unusableAccounts: {
    platform: string;
    name: string;
    status?: string;
    reason: 'unreachable' | 'no_advertiser';
    detail?: string;
  }[];
  /**
   * Platforms whose existing objects could not be read, so no name on them was checked.
   *
   * Deliberately not counted as a warning by the server: nothing about the plan is wrong.
   * It matters because with the read failed, "no name clashes" and "we never looked" arrive
   * looking identical, and only one of them means publishing will not make a second copy of
   * something already there.
   */
  uncheckedPlatforms: string[];
  canPublish: boolean;
}

/**
 * Re-runs the QA rule set on the server and takes the re-checked plan back.
 *
 * The stored findings go stale as soon as a reviewer edits a field, so this is the
 * only thing that makes the check meaningful — and the response carries the plan
 * because the issues on every node have just been rewritten.
 */
/**
 * Throws away the findings so the next run starts clean.
 *
 * Refetches the plan afterwards rather than patching it locally: the server keeps
 * the parse findings, so only it knows what is left.
 */
export function useClearPreflight(jobId: string) {
  const queryClient = useQueryClient();
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async () => {
      await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/preflight/clear`,
      });

      writePlan(await fetchPlan(jobId));
    },
    // Prefix invalidation: the job key is scoped by project too, which this hook
    // does not take, and the summary it refreshes hangs off the job read.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.all }),
  });
}

export function useRunPreflight(jobId: string) {
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async (): Promise<PreflightResult> => {
      const res = await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/preflight`,
      });

      const body = parseResponse(preflightSchema, res);
      writePlan(toJobPlan(body.plan));

      return {
        errors: body.errors,
        warnings: body.warnings,
        unboundPlatforms: body.unboundPlatforms,
        unusableAccounts: body.unusableAccounts,
        uncheckedPlatforms: body.uncheckedPlatforms,
        canPublish: body.canPublish,
      };
    },
  });
}

/**
 * Asks the platforms what they are doing with everything this job published.
 *
 * Separate from reading the report because it is a live call out to the platforms, not
 * a read of what we stored: review runs for hours after a publish, so this is the only
 * thing that can turn "12 created" into "3 serving, 9 refused, and here is why".
 */
export function useCheckDeliveryStatus(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<JobPublishReport> => {
      const res = await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/delivery-status`,
      });
      return toPublishReport(parseResponse(jobPublishReportSchema, res));
    },
    // The rows draw their status from what the platform last said, which this has just
    // rewritten. Without the refetch a change on the platform showed in the report only.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobPlan(jobId) }),
  });
}

export function useBindJobAccount(jobId: string) {
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async (input: BindJobAccountInput) => {
      await mediaHttp.patch({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/platforms/${input.platform}`,
        body: { connectorId: input.connectorId },
      });

      return fetchPlan(jobId);
    },
    onSuccess: writePlan,
  });
}

/**
 * Takes a platform off the job, with the rows it held.
 *
 * A platform with no account blocks Publish, so one added by mistake used to leave a
 * finished plan unpublishable behind an empty tab.
 */
export function useRemoveJobPlatform(jobId: string) {
  const writePlan = useWritePlan(jobId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (platform: string) => {
      await mediaHttp.delete({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/platforms/${platform}`,
      });

      return fetchPlan(jobId);
    },
    onSuccess: (plan) => {
      writePlan(plan);
      // The job list draws a job's platforms as icons, so removing one here makes that
      // list wrong until something else refetches it — and going back to it is exactly
      // what someone does next. This is the only mutation that changes which platforms a
      // job has; binding an account or a page changes what is on one.
      void queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.allJobs() });
    },
  });
}

/**
 * Removes a job from the list.
 *
 * A soft delete on the API: the row goes, the record and its audit trail stay. Anything
 * the job already published stays live on the platform too, which is why the caller
 * confirms first — deleting the job is losing track of those objects, not undoing them.
 */
export function useDeleteJob(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      await mediaHttp.delete({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}`,
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.jobs(projectId) }),
  });
}

/**
 * Adds one blank row under an ad set already in the plan.
 *
 * A late ad is ordinary — one more creative after the sheet was signed off — and the
 * alternative was a one-line spreadsheet uploaded as a second job.
 */
export function useAddJobRow(jobId: string) {
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async (parentId: string) => {
      await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/nodes/${parentId}/rows`,
      });

      return fetchPlan(jobId);
    },
    onSuccess: writePlan,
  });
}

/**
 * Chooses the client account a platform buys for under a manager account, for the whole job.
 * The id alone is sent: the server checks it against the platform and stores the platform's
 * own name for it.
 */
export function useBindJobAdvertiser(jobId: string) {
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async (input: BindJobAdvertiserInput) => {
      await mediaHttp.patch({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/platforms/${input.platform}`,
        body: { advertiserId: input.advertiserId },
      });

      return fetchPlan(jobId);
    },
    onSuccess: writePlan,
  });
}

/** Chooses the page a platform's ads publish as, for the whole job. */
export function useBindJobPage(jobId: string) {
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async (input: BindJobPageInput) => {
      await mediaHttp.patch({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/platforms/${input.platform}`,
        body: { pageId: input.pageId, pageName: input.pageName },
      });

      return fetchPlan(jobId);
    },
    onSuccess: writePlan,
  });
}

/**
 * Starts a publish. The writes happen on a worker, so this returns as soon as the
 * run is queued and the job is left in `publishing`.
 *
 * The caller watches the job status for the outcome rather than awaiting it here:
 * a plan of a few hundred rows is thousands of sequential platform calls, far
 * longer than any request should be held open.
 */
export function usePublishJob(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/publish`,
      });
    },
    onSettled: () => {
      // Prefix invalidation: the job key is scoped by project too, which this hook
      // does not know. Refetching is what starts the poll, since the job now reads
      // as `publishing`.
      void queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.all });
    },
  });
}

/**
 * Asks the platform whether it would accept the plan, creating nothing.
 *
 * Queued like a publish, and for the same reason: it is one call per checkable
 * object. The findings land on the plan rows, so the caller refetches the plan
 * once the run settles rather than reading anything from this response.
 */
export function useDryRunJob(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/dry-run`,
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.all });
    },
  });
}

/**
 * The report for a run that has finished.
 *
 * Fetched rather than returned by the publish call, because the run outlives the
 * request that started it. The plan is refetched alongside it on any outcome: a
 * partial publish writes a platform id and an error onto the nodes it touched, and
 * those are what a reviewer needs before trying again.
 */
export function useFetchPublishReport(jobId: string) {
  const writePlan = useWritePlan(jobId);

  return useMutation({
    mutationFn: async (): Promise<JobPublishReport> => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/delivery-jobs/${jobId}/publish-report`,
      });

      writePlan(await fetchPlan(jobId));

      return toPublishReport(parseResponse(jobPublishReportSchema, res));
    },
  });
}

async function fetchPlan(jobId: string): Promise<JobPlan> {
  const res = await mediaHttp.get({
    responseType: 'json',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: `/api/v1/delivery-jobs/${jobId}/plan`,
  });
  return toJobPlan(parseResponse(jobPlanSchema, res));
}

function creativeIdOf(plan: JobPlan | undefined, adNodeId: string): string | undefined {
  const find = (nodes: JobPlatformGroup['nodes']): string | undefined => {
    for (const node of nodes) {
      if (node.id === adNodeId) return node.creativeNodeId;
      const found = find(node.children);
      if (found) return found;
    }
    return undefined;
  };
  return (plan?.groups ?? []).map((group) => find(group.nodes)).find(Boolean);
}
